import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { TrigrammeTypeCertification } from '../models/trigramme-type-certification.model';
import { CreateRequestTrigrammeType } from '../request/create-request';
import { TrigrammeTypeCertificationService } from '../trigramme-type-certification.service';

export function avecMéthodesTrigrammeTypeCertification<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<TrigrammeTypeCertification>>(),
      props: type<EntityProps<TrigrammeTypeCertification>>(),
    },
    withMethods((store) => ({
      chargerTrigrammeTypeCertifications: (state: { trigrammeTypeCertifications: TrigrammeTypeCertification[] }) =>
        patchState(store, setAllEntities(state.trigrammeTypeCertifications)),
      ajouterTrigrammeType: (state: { trigrammeType: TrigrammeTypeCertification }) =>
        patchState(store, upsertEntity(state.trigrammeType)),
      modifierTrigrammeType: (state: { trigrammeType: TrigrammeTypeCertification }) =>
        patchState(store, updateEntity({ id: state.trigrammeType.id, changes: state.trigrammeType })),
      supprimerTrigrammeType: (id: string) => patchState(store, removeEntity(id)),
    })),
    withMethods((store, service = inject(TrigrammeTypeCertificationService)) => ({
      charger: rxMethod<void>(
        pipe(
          switchMap(() =>
            service.trouverTous().pipe(
              tapResponse({
                next: (response) => {
                  store.chargerTrigrammeTypeCertifications({ trigrammeTypeCertifications: response.items });
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors du chargement des trigramme de type des certifications, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error('Error lors du chargement des TrigrammeTypeCertifications via le Store: ', err);
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{ trigrammeType: TrigrammeTypeCertification; next: () => void }>(
        pipe(
          tap(({ trigrammeType }) => store.modifierTrigrammeType({ trigrammeType })),
          switchMap(({ trigrammeType, next }) =>
            service.modifier(trigrammeType).pipe(
              tapResponse({
                next: () => next(),
                error: (err: HttpErrorResponse) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la modification du trigramme,  contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      ajouter: rxMethod<{
        data: CreateRequestTrigrammeType;
        next?: () => void;
      }>(
        pipe(
          switchMap(({ data, next }) =>
            service.créer(data).pipe(
              tapResponse({
                next: () => {
                  next?.();
                },
                error: (err: HttpErrorResponse) => {
                  console.error("Error lors de l'ajout du trigramme via le Store: ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de l'ajout du trigramme, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<string>(
        pipe(
          switchMap((id) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => store.supprimerTrigrammeType(id),
                error: (err: HttpErrorResponse) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la suppression du trigramme, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      export: rxMethod<{ next: (blob: Blob) => void; error: () => void }>(
        pipe(
          switchMap(({ next, error }) => {
            return service.exporter().pipe(
              tapResponse({
                next: (blob: Blob) => {
                  next(blob);
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors des trigrammes type de certification, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            );
          }),
        ),
      ),
    })),
  );
}
